import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaTrash, FaUser, FaEnvelope, FaCreditCard, FaCalendar, FaArrowLeft } from 'react-icons/fa';
import AdminAPI from '../../utils/adminApi';
import terms from '../../config/terminology';

const DonatorManagement = () => {
  const [donators, setDonators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchDonators();
  }, []);

  const fetchDonators = async () => {
    try {
      setLoading(true);
      const data = await AdminAPI.get('/api/manage/donators');
      setDonators(data.donators);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (donatorId) => {
    try {
      setDeleting(true);
      const result = await AdminAPI.delete(`/api/manage/donators/${donatorId}`);
      alert(result.message);
      
      // Refresh the list
      await fetchDonators();
      setDeleteConfirm(null);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-xl">Loading {terms.donators}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        <Link to="/manage" className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-6 transition">
          <FaArrowLeft />
          <span>Back to Admin Dashboard</span>
        </Link>
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{terms.Donator} Management</h1>
          <p className="text-gray-400">Manage {terms.donator} accounts and their Stripe customer profiles</p>
        </div>

        <div className="bg-gray-900 rounded-lg border border-gray-800 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">All {terms.Donators} ({donators.length})</h2>
            <button
              onClick={fetchDonators}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition"
            >
              Refresh
            </button>
          </div>

          {donators.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No {terms.donators} found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left p-4">ID</th>
                    <th className="text-left p-4">Name</th>
                    <th className="text-left p-4">Email</th>
                    <th className="text-left p-4">Stripe Customer</th>
                    <th className="text-left p-4">Created</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {donators.map((donator) => (
                    <tr key={donator.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="p-4 text-gray-400">#{donator.id}</td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <FaUser className="text-gray-500" />
                          <span>{donator.first_name} {donator.last_name}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <FaEnvelope className="text-gray-500" />
                          <span className="text-blue-400">{donator.email}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <FaCreditCard className="text-gray-500" />
                          <span className="font-mono text-sm">
                            {donator.stripe_customer_id || 'No Stripe account'}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <FaCalendar className="text-gray-500" />
                          <span className="text-sm">{formatDate(donator.created_at)}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => setDeleteConfirm(donator)}
                          className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition"
                          title={`Delete ${terms.donator}`}
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-900 p-6 rounded-lg border border-gray-800 max-w-md w-full mx-4">
              <h3 className="text-xl font-semibold mb-4">Confirm Deletion</h3>
              <p className="text-gray-400 mb-2">
                Are you sure you want to delete this {terms.donator}?
              </p>
              <div className="bg-gray-800 p-4 rounded mb-4">
                <p className="font-semibold">{deleteConfirm.first_name} {deleteConfirm.last_name}</p>
                <p className="text-sm text-gray-400">{deleteConfirm.email}</p>
                {deleteConfirm.stripe_customer_id && (
                  <p className="text-xs text-gray-500 mt-1">
                    Stripe: {deleteConfirm.stripe_customer_id}
                  </p>
                )}
              </div>
              <p className="text-sm text-yellow-400 mb-4">
                ⚠️ This will also delete their Stripe customer account and cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg font-semibold transition disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DonatorManagement;